import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { ERROR_STRINGS } from '@/shared/constants/strings';
import { ADMIN, AUTH, USER } from '@/shared/constants/api-endpoints';
import { useUIStore } from '@/shared/stores/ui.store';
import { queryClient } from '@/shared/lib/queryClient.config';
import { authQueryKeys } from '@/shared/hooks/queries/queryKeys';
import { triggerAuthRedirect } from '@/shared/lib/authRedirect';

/**
 * Backend error envelope. `error` may be a plain string (legacy) or an object carrying a
 * machine-readable `code` (e.g. `{ code: 'CONSENT_REQUIRED' }`) / `statusCode`.
 */
interface ApiErrorResponse {
  message?: string;
  error?: string | { code?: string; statusCode?: number };
}

/** Pull a string message out of the backend `error` field only when it is a string. */
const errorAsString = (err: ApiErrorResponse['error']): string | null =>
  typeof err === 'string' ? err : null;

/** Pull the machine-readable code out of the backend `error` object. */
const errorCode = (err: ApiErrorResponse['error']): string | undefined =>
  err && typeof err === 'object' ? err.code : undefined;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Once the refresh token is confirmed dead, skip further refresh attempts so
// TanStack Query retries don't cause a barrage of refresh-token calls.
let isSessionExpired = false;

/** Call this after a successful login to re-enable the refresh flow. */
export function resetSessionExpiry(): void {
  isSessionExpired = false;
}

const processQueue = (error: AxiosError | null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  }
  failedQueue = [];
};

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

/**
 * Handles a 401 by refreshing the token and retrying the original request —
 * queueing concurrent 401s behind a single in-flight refresh, and redirecting
 * to login if the refresh itself fails. Called only when the caller has
 * already confirmed this is a refresh-eligible 401 (see the interceptor).
 */
async function handleUnauthorizedRefresh(
  error: AxiosError,
  originalRequest: RetryableRequestConfig,
  isPublicAuthRoute: boolean
) {
  // Session already confirmed expired — don't attempt another refresh.
  // This prevents TanStack Query retries from triggering a barrage of
  // refresh-token calls after the first refresh has already failed.
  if (isSessionExpired) {
    throw error;
  }

  if (isRefreshing) {
    // If already refreshing, queue this request
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => {
        return axiosInstance(originalRequest);
      })
      .catch((err) => {
        throw err;
      });
  }

  isRefreshing = true;

  try {
    // Attempt to refresh the token
    await axiosInstance.post(AUTH.REFRESH_TOKEN);

    // Token refresh successful, process queued requests
    processQueue(null);
    isRefreshing = false;

    // Retry the original request — mark as retried so a second 401
    // (e.g. permissions error) doesn't trigger another refresh cycle.
    return axiosInstance(Object.assign(originalRequest, { _retry: true }));
  } catch (refreshError) {
    // Refresh failed — mark session expired so retries skip the refresh path.
    isSessionExpired = true;
    processQueue(error);
    isRefreshing = false;

    // Redirect to login page (but not if already on a public auth route)
    if (
      typeof globalThis !== 'undefined' &&
      !isPublicAuthRoute &&
      !globalThis.location.pathname.includes('/login')
    ) {
      // Clear the query cache so TanStack Query retries don't queue up
      // further requests while we're navigating away.
      queryClient.clear();
      toast.warning(ERROR_STRINGS.SESSION_EXPIRED);
      triggerAuthRedirect();
    }

    throw refreshError;
  }
}

/**
 * Handles a consent-required 403 (user withdrew consent but a gated action slipped
 * through, e.g. stale cache / another tab). The backend tags these with `error.code`
 * 'CONSENT_REQUIRED', so detection is endpoint-agnostic. Throws the (annotated) error
 * when handled so it never reaches the generic /login redirect; returns normally
 * (does nothing) when the 403 isn't a consent block.
 */
function handleConsentRequired403(
  error: AxiosError,
  responseData: ApiErrorResponse | undefined,
  isPublicAuthRoute: boolean,
  originalRequest: RetryableRequestConfig | undefined
): void {
  const isConsent403 =
    error.response?.status === 403 &&
    errorCode(responseData?.error) === 'CONSENT_REQUIRED' &&
    // Don't surface the app-wide consent modal during login/registration — an
    // unauthenticated visitor on a public auth route has no session to gate,
    // and the error still surfaces normally on the auth screen.
    !isPublicAuthRoute;

  if (!isConsent403) return;

  (error as AxiosError & { isConsentRequired?: boolean }).isConsentRequired =
    true;
  useUIStore.getState().openModal('consentRequired');
  // Refetch the profile so the in-place gate (status/needsReConsent) updates too —
  // but NOT when the failing request IS the profile read itself, or the
  // invalidate would refetch it, get the same CONSENT_REQUIRED 403, and loop
  // (re-opening the modal and re-invalidating endlessly).
  const isProfileRequest = originalRequest?.url?.includes(USER.PROFILE);
  if (!isProfileRequest) {
    queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser });
  }
  throw error;
}

/** Business-rule 403s (e.g. system-managed knowledge sources rejecting an
 * upload/delete/retry) are not auth failures — the user is still logged
 * in and should see the backend's message in place, not get bounced to
 * /login. The versions/* prefix intentionally covers both the delete and
 * retry endpoints, since both are gated by the same system-managed-source
 * rule on the backend. */
function isBusinessRuleForbidden403(
  status: number | undefined,
  originalRequest: RetryableRequestConfig | undefined
): boolean {
  const businessRuleForbiddenUrls = [
    ADMIN.KNOWLEDGE.INITIATE_UPLOAD,
    ADMIN.KNOWLEDGE.CONFIRM_UPLOAD,
  ];
  return (
    status === 403 &&
    (businessRuleForbiddenUrls.some((url) =>
      originalRequest?.url?.includes(url)
    ) ||
      !!originalRequest?.url?.includes('/knowledge/admin/sources/versions/'))
  );
}

/** Whether an unhandled 401/403 should bounce the user to the login page. */
function shouldRedirectToLogin(
  status: number | undefined,
  isSkipRefreshUrl: boolean,
  isPublicAuthRoute: boolean,
  isBusinessRuleForbidden: boolean
): boolean {
  return (
    (status === 401 || status === 403) &&
    !isSkipRefreshUrl &&
    !isPublicAuthRoute &&
    !isBusinessRuleForbidden
  );
}

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error: AxiosError) => {
    throw error;
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Skip refresh logic for certain endpoints (including MFA endpoints)
    const skipRefreshUrls = [
      AUTH.LOGIN,
      AUTH.SIGNUP,
      AUTH.REFRESH_TOKEN,
      AUTH.VERIFY_OTP,
      AUTH.RESEND_OTP,
      AUTH.ACTIVATE,
      AUTH.PASSWORD_RESET_REQUEST,
      AUTH.PASSWORD_RESET_COMPLETE,
    ];
    const isSkipRefreshUrl = skipRefreshUrls.some((url) =>
      originalRequest?.url?.includes(url)
    );

    // Public auth routes that should not redirect to login
    const publicAuthRoutes = [
      '/login',
      '/register',
      '/verify-otp',
      '/auth/set-password',
      '/auth/forgot-password',
      '/auth/reset-password',
    ];
    const isPublicAuthRoute = publicAuthRoutes.some((route) =>
      globalThis?.location?.pathname.includes(route)
    );

    // Handle 401 errors with token refresh
    if (
      error.response?.status === 401 &&
      !isSkipRefreshUrl &&
      originalRequest &&
      !(originalRequest as RetryableRequestConfig)._retry
    ) {
      return handleUnauthorizedRefresh(
        error,
        originalRequest,
        isPublicAuthRoute
      );
    }

    // Extract error message from backend response or use default
    const responseData = error.response?.data as ApiErrorResponse | undefined;
    const backendMessage =
      responseData?.message || errorAsString(responseData?.error) || null;

    if (error.response) {
      error.message = backendMessage || ERROR_STRINGS.DEFAULT_API_ERROR;
    } else {
      error.message = ERROR_STRINGS.NETWORK_ERROR;
    }

    // Consent-required 403 — handled and THROWN EARLY inside this helper so it
    // never reaches the generic /login redirect below.
    handleConsentRequired403(
      error,
      responseData,
      isPublicAuthRoute,
      originalRequest
    );

    const isBusinessRuleForbidden = isBusinessRuleForbidden403(
      error.response?.status,
      originalRequest
    );

    // Redirect to login for unauthenticated users (global handler)
    // But NOT on public auth routes (login, register, verify-otp, set-password)
    if (
      shouldRedirectToLogin(
        error.response?.status,
        isSkipRefreshUrl,
        isPublicAuthRoute,
        isBusinessRuleForbidden
      )
    ) {
      if (
        typeof globalThis !== 'undefined' &&
        !globalThis.location.pathname.includes('/login')
      ) {
        triggerAuthRedirect();
      }
    }

    // Capture unexpected errors — 401/403 are fully handled above (auth refresh,
    // consent modal, login redirect); 400/422 are client validation errors shown
    // in the UI. Everything else (5xx, network failures, 404, 429, etc.) is
    // unexpected and worth tracking in Sentry.
    const status = error.response?.status;
    const isHandledError =
      status === 400 || status === 401 || status === 403 || status === 422;

    if (!isHandledError) {
      Sentry.captureException(error, {
        extra: {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status,
        },
      });
    }

    throw error;
  }
);

export default axiosInstance;
